import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { AnimatedRoutes } from "@/components/AnimatedRoutes";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Maintenance from "@/pages/Maintenance";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean | null>(null);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await supabase
          .from("site_settings")
          .select("value")
          .eq("key", "maintenance_mode")
          .single();
        setIsMaintenanceMode(data?.value === "true");
      } catch {
        setIsMaintenanceMode(false);
      }
    };
    check();
  }, []);

  if (isMaintenanceMode === null) return null; // loading
  if (isMaintenanceMode) return <Maintenance />;
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <MaintenanceGate>
      <AuthProvider>
        <TooltipProvider delayDuration={300}>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AnimatedRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </MaintenanceGate>
  </QueryClientProvider>
);

export default App;
